import { FaBrain, FaCogs, FaDatabase, FaSearch } from 'react-icons/fa';
import { Handle, Position, useStore } from 'reactflow';
import SATooltip from '../common/SATooltip';
import { toolNodes } from './NodePalette';
import logo_icon from '../../assets/img/logo_icon.png';
import litellmIcon from '../../assets/img/litellm.png';
import customLlmIcon from '../../assets/img/custom-llm.svg';
import {
  SiGooglegemini,
  SiOllama
} from 'react-icons/si';
import { BsOpenai } from 'react-icons/bs';
import { VscAzure } from "react-icons/vsc";
import { useState, useEffect } from 'react';
import ToolIcon from '../Custom/ToolIcon';
import { showNotificationGlobal } from '../common/NotificationProvider';

const icons = {
  default: <img src={logo_icon} width={35} height={25} alt="" />,
  llm: <FaBrain size={25} />,
  openai: <BsOpenai size={25} />,
  ollama: <SiOllama size={25} />,
  'azure-openai': <VscAzure size={25} />,
  litellm: <img src={litellmIcon} width={50} height={40} alt="" />,
  gemini: <SiGooglegemini size={25} />,
  custom: <img src={customLlmIcon} width={28} height={28} alt="Custom LLM" style={{ objectFit: 'contain' }} />,
  memory: <FaDatabase size={25} />,
  tool: <FaCogs size={25} />,
  search: <FaSearch size={25} />,
  'azure_ai': <VscAzure size={25} />,
  'azure': <VscAzure size={25} />
};

const colors = {
  llm: '#1976d2',
  memory: '#388e3c',
  tool: '#fbc02d',
  search: '#a259e6',
};

const isInteractiveSelector = (s) =>
  s.nodesDraggable || s.nodesConnectable || s.elementsSelectable;

export default function ConfigNode({ data, selected, id }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [highlightColor, setHighlightColor] = useState('green');
  const [currentIcon, setCurrentIcon] = useState(null);
  const [currentColor, setCurrentColor] = useState(null);
  const [currentLabel, setCurrentLabel] = useState(null);

  const type = data?.configType || 'tool';
  let color = colors[type] || '#a259e6';
  let icon = icons[type];
  let label = data?.displayName || data?.handlerName;

  // Handle node highlighting events (for LLM nodes)
  useEffect(() => {
    function handleHighlight(e) {
      if (e.detail.nodeId === id && e.detail.nodeType === 'config' && type === 'llm') {
        setIsHighlighted(true);
        setHighlightColor(e.detail.color || 'green');
      }
    }

    function handleStopHighlight() {
      setIsHighlighted(false);
    }

    window.addEventListener('node-highlight', handleHighlight);
    window.addEventListener('node-highlight-stop-all', handleStopHighlight);
    
    return () => {
      window.removeEventListener('node-highlight', handleHighlight);
      window.removeEventListener('node-highlight-stop-all', handleStopHighlight);
    };
  }, [id, type]);

  const isInteractive = useStore(isInteractiveSelector);

  // Handler for delete button
  const handleDelete = (e) => {
    e.stopPropagation();
    if (!isInteractive){
      showNotificationGlobal('Cannot delete this component because the workflow is locked.', 'error', 'bottomRight');
      return;
    }
    window.dispatchEvent(new CustomEvent('delete-node', {
      detail: { nodeId: id, nodeType: 'config' }
    }));
  };

// Effect to update icon when data changes
  useEffect(() => {
    if (type === 'tool' && data?.handlerName) {
      // Use icon from data prop first (this should be available immediately)
      if (data.icon) {
        setCurrentIcon(data.icon);
        setCurrentColor(data?.iconColor || data?.color || colors.tool);
        setCurrentLabel(data?.displayName || data?.handlerName);
      } else {
        // Fallback to toolNodes if icon not in data
        let toolConfig = toolNodes.find(t => t.handlerName === data.handlerName);
        if (toolConfig?.icon) {
          setCurrentIcon(toolConfig.icon);
          setCurrentColor(data?.iconColor || toolConfig?.iconColor || toolConfig?.color || colors.tool);
          setCurrentLabel(data?.displayName || toolConfig?.displayName || data?.handlerName);
        } else {
          // If no icon found, use default
          setCurrentIcon(icons.tool);
          setCurrentColor(data?.iconColor || data?.color || colors.tool);
          setCurrentLabel(data?.displayName || data?.handlerName);
        }
      }
    } else {
      setCurrentIcon(
        data?.llmType === 'litellm'
          ? data?.model?.split('/')[0] || icons.llm
          : icon
      );
      setCurrentColor(color);
      setCurrentLabel(data?.llmType === 'litellm' ? data?.model : label);
    }
  }, [data, type, color, icon, label]);

  // Use state values if available, otherwise fall back to computed values
  const displayIcon = currentIcon || icon;
  const displayColor = currentColor || color;
  const displayLabel = currentLabel || label;

  // Function to truncate text with ellipsis
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // If this is an LLM node, make it a blue rectangle with rounded corners
  if (type === 'llm') {
    const truncatedLabel = truncateText(displayLabel, 25);
    return (
      <div 
        title=""
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}}
      >
      <div style={{ position: 'relative' }}>
      <div
        style={{
          background: '#e3f2fd',
          border: isHighlighted 
            ? `3px solid ${highlightColor === 'red' ? '#dc3545' : '#28a745'}` 
            : selected 
              ? `1.5px solid #1976d2` 
              : `1px dashed #1976d2`,
          boxShadow: isHighlighted 
            ? `0 0 20px ${highlightColor === 'red' ? 'rgba(220, 53, 69, 0.6)' : 'rgba(40, 167, 69, 0.6)'}, 0 2px 8px rgba(0,0,0,0.08)`
            : selected 
              ? 'rgba(0, 0, 0, 0.08) 0px 1px 0px 7px' 
              : '0 2px 8px rgba(0,0,0,0.08)',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: data?.iconColor,
          padding: '10px 25px',
          animation: isHighlighted ? 'pulse 1.5s ease-in-out infinite' : 'none'
        }}
        title=""
      >
        <span title={truncatedLabel}>
            {
              data?.llmType === 'litellm'
                ? icons[data?.model?.split('/')?.[0]] || icons.llm
                : icons[data?.llmType] || icons.llm
            }
          </span>
        <Handle type="target" position={Position.Top} style={{ background: '#1976d2', width: 12, height: 12 }} title="" />
      </div>

        {/* Delete button that appears on hover - top right corner */}
        {isHovered && (
          <div
            style={{
              position: 'absolute',
              top: -10,
              right: -10,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#fff',
              border: '1px solid #6c757d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 10,
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            onClick={handleDelete}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.borderColor = '#495057';
              e.currentTarget.style.background = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.borderColor = '#6c757d';
              e.currentTarget.style.background = '#fff';
            }}
          >
            <i className="fas fa-trash" style={{ 
              color: '#6c757d', 
              fontSize: '8px',
              pointerEvents: 'none'
            }}></i>
          </div>
        )}
      </div>

        <SATooltip title={displayLabel} placement="bottom">
          <div className='font-semi-bold' style={{
            fontSize: 12,
            marginTop: 4,
            textAlign: 'center',
            overflow: 'hidden',
            whiteSpace: 'nowrap'
          }}>
            {truncatedLabel}
          </div>
        </SATooltip>

        {type === 'llm' && (
          <style>
            {`
              @keyframes pulse {
                0% {
                  box-shadow: 0 0 20px ${highlightColor === 'red' ? 'rgba(220, 53, 69, 0.6)' : 'rgba(40, 167, 69, 0.6)'}, 0 2px 8px rgba(0,0,0,0.08);
                }
                50% {
                  box-shadow: 0 0 30px ${highlightColor === 'red' ? 'rgba(220, 53, 69, 0.8)' : 'rgba(40, 167, 69, 0.8)'}, 0 2px 8px rgba(0,0,0,0.08);
                }
                100% {
                  box-shadow: 0 0 20px ${highlightColor === 'red' ? 'rgba(220, 53, 69, 0.6)' : 'rgba(40, 167, 69, 0.6)'}, 0 2px 8px rgba(0,0,0,0.08);
                }
              }
            `}
          </style>
        )}
      </div>
    );
  }

  const truncatedLabel = truncateText(displayLabel, 15);
  const nodeSize = truncatedLabel.length <= 12 ? 60 : Math.min(80, 60 + truncatedLabel.length);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ position: 'relative',display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
    <div
      style={{
        background: '#fff',
        border: selected ? `2px solid ${displayColor}` : `1px dashed ${displayColor}`,
        boxShadow: selected ? 'rgba(0, 0, 0, 0.08) 0px 1px 0px 7px'  : '0 2px 8px rgba(0,0,0,0.08)',
        borderRadius: '50%',
        width: nodeSize,
        height: nodeSize,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
        <span className="palette-node-icon" style={{ color: displayColor, pointerEvents: 'none' }}>
          <ToolIcon tool={data} />
        </span>
      <Handle type="target" position={Position.Top} style={{ background: displayColor, width: 12, height: 12 }} />
    </div>
    <SATooltip title={displayLabel} placement="bottom">
        <div className='font-semi-bold' style={{ 
          fontSize: 12, 
          marginTop: 4,
          textAlign: 'center',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}>
          {truncatedLabel}
        </div>
      </SATooltip>
      
      {/* Delete button that appears on hover - top right corner */}
      {isHovered && (
        <div
          style={{
            position: 'absolute',
            top: -10,
            right: -10,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#fff',
            border: '1px solid #6c757d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          onClick={handleDelete}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.borderColor = '#495057';
            e.currentTarget.style.background = '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.borderColor = '#6c757d';
            e.currentTarget.style.background = '#fff';
          }}
        >
          <i className="fas fa-trash" style={{ 
            color: '#6c757d', 
            fontSize: '9px',
            cursor: 'pointer'
          }}></i>
        </div>
      )}
    </div>
  );
} 